<template>
  <v-dialog
      v-model="dialog"
      max-width="640"
      persistent
  >
    <v-card>
      <v-card-title class="headline">
        Editar consultorio
        <v-spacer></v-spacer>
        <v-switch
            :color="office.status
                ? 'success'
                : 'secondary lighten-3'"
            v-model="office.status"
            label="Estado"
            class="mt-0 mb-2"
            hide-details
        >
          <template v-slot:label>
            <v-chip :color="office.status ? 'success' : 'secondary lighten-3'" small>
              {{ office.status ? 'Habilitado' : 'Deshabilitado' }}
            </v-chip>
          </template>
        </v-switch>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pt-5">
        <v-form ref="formEditOffice" @submit.prevent="updateOffice">
          <v-row>
            <v-col cols="12" sm="8">
              <v-text-field
                  v-model="office.name"
                  label="Nombre *"
                  outlined
                  dense
                  required
                  :rules="[rules.required, rules.counter]"
                  prefix="DIETEC"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                  v-model="office.code"
                  label="Código *"
                  outlined
                  dense
                  required
                  :rules="[rules.required, rules.code]"
                  counter
                  maxlength="8"
                  minlength="4"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                  v-model="office.province"
                  :items="provinces"
                  label="Estado *"
                  outlined
                  dense
                  required
                  :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                  v-model="office.city"
                  :items="cities"
                  label="Municipio *"
                  outlined
                  dense
                  required
                  :rules="[rules.required]"
                  :disabled="!office.province"
                  v-if="office.province !== 'Otro / Extranjero'"
              />
              <v-text-field
                  v-model="office.city"
                  label="Ciudad *"
                  outlined
                  dense
                  required
                  :rules="[rules.required]"
                  v-else
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                  v-model="office.district"
                  label="Colonia *"
                  outlined
                  dense
                  required
                  :rules="[rules.required, rules.counter]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                  v-model="office.street"
                  label="Calle y número*"
                  outlined
                  dense
                  required
                  :rules="[rules.required, rules.counter]"
              />
            </v-col>

            <!-- Dieticians -->
            <v-col cols="12" v-if="isAllowed(PERMISSIONS.OFFICE_ASSIGN)">
              <v-select
                  v-model="office.dieticians"
                  :items="availableDieticians"
                  item-text="name"
                  return-object
                  label="Dietistas"
                  outlined
                  dense
                  multiple
                  chips
                  deletable-chips
                  small-chips
                  no-data-text="No hay dietistas disponibles"
                  :loading="isLoadingDieticians"
                  :disabled="isLoadingDieticians"
              />
            </v-col>

            <!-- Leader -->
            <v-col cols="12" sm="6" v-if="isAllowed(PERMISSIONS.OFFICE_ASSIGN)">
              <v-select
                  v-model="office.leader"
                  :items="office.dieticians"
                  item-text="name"
                  return-object
                  label="Dietista líder"
                  outlined
                  dense
                  no-data-text="No hay dietistas disponibles"
                  :rules="officeHasDieticians ? [rules.required] : []"
                  :disabled="isLoadingDieticians || !office.dieticians || office.dieticians.length === 0"
              />
            </v-col>

            <v-col cols="12" class="pt-0">
              <v-list dense class="py-0">
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title class="caption grey--text">
                      Creado el {{ formatDate(originalOffice.createdAt) }} por {{ originalOffice.createdBy }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="caption grey--text" v-if="originalOffice.updatedAt">
                      Última actualización el {{ formatDate(originalOffice.updatedAt) }} por {{
                        originalOffice.updatedBy
                      }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <CancelButton
            :isDisabled="isLoading"
            @click="handleDialogClose"
        />
        <SaveButton
            :isLoading="isLoading"
            :isDisabled="isInvalidForm"
            @click="updateOffice"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from "vue";

import OfficeService from "@/services/offices/OfficeService";
import UserService from "@/services/users/UserService";

import throwSuccessMessage from "@/utils/SuccessMessage";
import throwErrorMessage from "@/utils/ErrorMessage";

import {cities, provinces} from "@/utils/Locations";

import {UpdatedOffice} from "@/services/offices/entities/UpdatedOffice";
import {DieticianName} from "@/services/users/entities/DieticianName";
import {PERMISSIONS} from "@/utils/Permissions";
import {isAllowed} from "@/utils/UniversalFunctions";
import CancelButton from "@/components/buttons/CancelButton.vue";
import SaveButton from "@/components/buttons/SaveButton.vue";

export default Vue.extend({
  name: "DialogEditOffice",
  components: {SaveButton, CancelButton},
  props: {
    originalOffice: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // Dialog
      dialog: false as boolean,

      // Office
      office: {} as UpdatedOffice,

      // Dieticians
      availableDieticians: [] as DieticianName[],

      // Address
      provinces: provinces,
      cities: [] as string[],

      // Loading
      isLoading: false as boolean,
      isLoadingDieticians: false as boolean,

      // Rules
      rules: {
        required: (value: string) => !!value || 'Campo requerido',
        counter: (value: string) => (value || '').length <= 50 || 'Máximo 50 caracteres',
        code: (value: string) => (value || '').length >= 4 && (value || '').length <= 8 || 'Mínimo 4 y máximo 8 caracteres',
      },
    };
  },

  watch: {
    'office.province': function (val: string) {
      this.cities = cities[val] || [];
    },
    originalOffice: {
      handler() {
        this.office = {...this.originalOffice};
      },
      immediate: true
    },
  },

  computed: {
    PERMISSIONS() {
      return PERMISSIONS
    },
    isInvalidForm(): boolean {
      return (
          !this.office.name ||
          !this.office.code ||
          !this.office.province ||
          !this.office.city ||
          !this.office.district ||
          !this.office.street
      );
    },
    officeHasDieticians(): boolean {
      return this.office.dieticians && this.office.dieticians.length > 0;
    }
  },

  methods: {
    async updateOffice() {
      this.isLoading = true;
      try {
        const form = this.$refs.formEditOffice as any;
        if (!form.validate()) {
          throwErrorMessage("Asegúrate de llenar todos los campos correctamente");
          return;
        }

        if (this.office.name.toUpperCase().startsWith("DIETEC")) {
          throwErrorMessage("El nombre del consultorio no puede empezar con DIETEC");
          return;
        }
        const body = {
          id: this.office.id,
          name: this.office.name,
          code: this.office.code,
          province: this.office.province,
          city: this.office.city,
          district: this.office.district,
          street: this.office.street,
          status: this.office.status,
          dieticians: this.office.dieticians || [],
          leader: this.office.leader || null,
        }
        await OfficeService.updateOffice(body);
        throwSuccessMessage(`Se ha editado DIETEC ${this.office.name} correctamente`);
        this.$emit("office-created");
        this.handleDialogClose();
      } finally {
        this.isLoading = false;
      }
    },

    async getAvailableDieticians() {
      this.isLoadingDieticians = true;
      try {
        const body = {
          idOffice: this.office.id,
        }
        this.availableDieticians = await UserService.getAvailableDieticians(body);
      } finally {
        this.isLoadingDieticians = false;
      }
    },
    formatDate(date: string) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    isAllowed(permissionName: string) {
      return isAllowed(permissionName);
    },
    handleDialogClose() {
      const form = this.$refs.formEditOffice as any;
      this.office = {...this.originalOffice};
      form.resetValidation();
      this.dialog = false;
    },
  },

  async created() {
    this.provinces.push("Otro / Extranjero");
  }
});
</script>